<script lang="ts" setup>
	import { ref, watch } from 'vue'

	import SlideOver from '@/components/SlideOver.vue'
	import StyledCombobox from '@/components/StyledCombobox.vue'
	import StyledDateInput from '@/components/StyledDateInput.vue'
	import StyledSelect from '@/components/StyledSelect.vue'

	import { useAuth } from '@/composables/useAuth'

	import useCompaniesSearch from '@/modules/companies/composables/useCompaniesSearch'
	import { Company } from '@/modules/companies/interfaces/company'
	import useUsersSearch from '@/modules/users/composables/useUsersSearch'
	import { User } from '@/modules/users/interfaces/user'

	import type { PlaneFilters } from '../pages/PlaneReportsPage.vue'

	interface Props {
		showFilters: boolean
		selectedCompany: Company | null
		filtres: PlaneFilters
	}

	interface Emits {
		(e: 'update:showFilters', value: boolean): void
		(e: 'update:selectedCompany', value: Company | null): void
		(e: 'update:filtres', filtres: Props['filtres']): void
	}

	const props = defineProps<Props>()

	const emit = defineEmits<Emits>()

	const { user } = useAuth()

	const localSelectedCompany = ref<Company | null>(props.selectedCompany)

	const selectedUser = ref<User | null>(null)

	watch(
		() => props.selectedCompany,
		(newValue) => {
			localSelectedCompany.value = newValue
		}
	)

	const {
		companies,
		setQuery,
		isLoading: loadingCompanies,
	} = useCompaniesSearch({
		immediate: true,
		params: {
			corporate: user.value?.corporationId,
		},
	})

	const {
		data: users,
		isLoading: loadingUsers,
		isFetching: isFetchingUsers,
		setQuery: setUsersQuery,
		refetch: refetchUsers,
	} = useUsersSearch({
		automaticSearch: false,
		immediate: false,
		params: {
			filters: {
				company:
					localSelectedCompany.value ?
						localSelectedCompany.value.id
					:	undefined,
			},
		},
	})

	const optionsFilterMoths = [
		{ label: 'Último mes', value: '1month' },
		{ label: 'Últimos 3 meses', value: '3months' },
		{ label: 'Últimos 6 meses', value: '6months' },
		{ label: 'Último año', value: 'lastYear' },
		{ label: 'Próximo mes', value: '1monthNext' },
		{ label: 'Próximos 3 meses', value: '3monthNext' },
		{ label: 'Próximos 6 meses', value: '6monthNext' },
		{ label: 'Este año', value: 'year' },
		{ label: 'Personalizado', value: 'custom' },
	]

	const datePresentChange = (preset: string) => {
		const today = new Date()
		let startDate: Date = today
		let endDate: Date = today

		if (preset === 'custom') return
		if (preset === 'year') {
			const year = today.getFullYear()
			startDate = new Date(year, 0, 1)
			endDate = new Date(year, 11, 31)
			return { startDate, endDate }
		}
		if (preset === 'lastYear') {
			const lastYear = today.getFullYear() - 1
			startDate = new Date(lastYear, 0, 1)
			endDate = new Date(lastYear, 11, 31)
			return { startDate, endDate }
		}

		const monthsMatch = preset.match(/(\d+)month/)
		if (!monthsMatch) return

		const months = parseInt(monthsMatch[1])
		const isNext = preset.includes('Next')

		if (isNext) {
			startDate = new Date(today.getFullYear(), today.getMonth(), 1)
			endDate = new Date(today.getFullYear(), today.getMonth() + months, 0)
		} else {
			startDate = new Date(today.getFullYear(), today.getMonth() - months, 1)
			endDate = new Date(today.getFullYear(), today.getMonth(), 0)
		}

		return { startDate, endDate }
	}

	const applyDatePreset = (preset: string) => {
		const dates = datePresentChange(preset)
		if (!dates) return 'custom'

		const newDateRange = [
			dates.startDate.toISOString().split('T')[0],
			dates.endDate.toISOString().split('T')[0],
		]
		emit('update:filtres', { ...props.filtres, dateRange: newDateRange })
	}

	const applyCreationDatePreset = (preset: string) => {
		const dates = datePresentChange(preset)
		if (!dates) return 'custom'

		const newCreationDateRange = [
			dates.startDate.toISOString().split('T')[0],
			dates.endDate.toISOString().split('T')[0],
		]
		emit('update:filtres', {
			...props.filtres,
			creationDateRange: newCreationDateRange,
		})
	}

	watch(
		() => props.filtres.datePreset,
		(newPreset) => {
			applyDatePreset(newPreset)
		},
		{ immediate: true }
	)

	watch(
		() => props.filtres.creationDatePreset,
		(newPreset) => {
			if (!newPreset || newPreset === '') {
				emit('update:filtres', {
					...props.filtres,
					creationDateRange: undefined,
				})
				return
			}
			applyCreationDatePreset(newPreset)
		}
	)
</script>

<template>
	<SlideOver
		:when="showFilters"
		title="Filtros de Reportes"
		:hide-buttons="true"
		@update:close-slide-over="() => emit('update:showFilters', false)"
	>
		<div class="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 min-h-full">
			<!-- Header -->
			<div class="mb-8 relative">
				<div
					class="absolute inset-0 bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl opacity-30 transform -skew-y-1"
				></div>
				<div
					class="relative bg-white rounded-xl p-6 border border-gray-200 shadow-sm"
				>
					<div class="flex items-center gap-4 mb-3">
						<div class="p-3 bg-blue-100 rounded-lg">
							<span class="material-symbols-outlined text-blue-600">
								filter_list
							</span>
						</div>
						<div>
							<h3 class="text-xl font-bold text-gray-900">
								Filtros Avanzados
							</h3>
							<p class="text-sm text-gray-600 mt-1">
								Personaliza tus reportes de vuelos
							</p>
						</div>
					</div>
				</div>
			</div>

			<div class="space-y-8">
				<!-- Empresa -->
				<div class="relative">
					<div
						class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
					>
						<div class="flex items-center gap-3 mb-4">
							<div class="p-2 bg-purple-100 rounded-lg">
								<span class="material-symbols-outlined text-purple-600 text-xl">
									business
								</span>
							</div>
							<h4 class="font-semibold text-gray-900">
								Selección de Empresa
							</h4>
						</div>

						<StyledCombobox
							:modelValue="localSelectedCompany"
							@update:modelValue="
								(value: Company | null) => {
									localSelectedCompany = value
									emit('update:selectedCompany', value)
								}
							"
							label="Empresa"
							placeholder="Seleccionar empresa"
							:options="companies || []"
							:loading-options="loadingCompanies"
							:on-input-change="localSelectedCompany ? undefined : setQuery"
							:show-input="!localSelectedCompany"
							:prevent-auto-focus="true"
							:prevent-scroll-on-focus="false"
							class="mb-2"
						>
							<template #default="{ option }">
								<span>{{ option.name }}</span>
							</template>

							<template #input-overlap>
								<div class="flex items-center justify-between gap-2 w-full">
									<p class="text-sm normal-case md:text-base truncate">
										{{ localSelectedCompany?.name }}
									</p>
									<button
										class="material-symbols-outlined text-[22px] hover:text-gray-700 transition-colors"
										@click.prevent="
											() => {
												localSelectedCompany = null
												emit('update:selectedCompany', null)
											}
										"
									>
										close
									</button>
								</div>
							</template>
						</StyledCombobox>
					</div>
				</div>

				<!-- Fechas -->
				<div
					class="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
				>
					<div
						class="bg-gradient-to-r from-orange-50 to-red-50 px-6 py-4 border-b border-gray-200"
					>
						<div class="flex items-center gap-3">
							<div class="p-2 bg-orange-100 rounded-lg">
								<span
									class="material-symbols-outlined text-orange-600 text-xl"
								>
									calendar_month
								</span>
							</div>
							<h4 class="font-semibold text-gray-900">Filtros de Fecha</h4>
						</div>
					</div>

					<div class="p-6 space-y-6">
						<!-- Preset de fechas -->
						<div class="space-y-2">
							<div class="flex items-center gap-2 mb-2">
								<span
									class="material-symbols-outlined text-orange-600 text-base"
								>
									schedule
								</span>
								<span class="text-sm font-medium text-gray-700">
									Período de tiempo
								</span>
							</div>
							<StyledSelect
								:modelValue="filtres.datePreset"
								label=""
								:options="optionsFilterMoths"
								@update:modelValue="
									(value: string) =>
										emit('update:filtres', { ...filtres, datePreset: value })
								"
							/>
						</div>

						<!-- Rango personalizado -->
						<div
							v-if="filtres.datePreset === 'custom'"
							class="space-y-2"
						>
							<div class="flex items-center gap-2 mb-2">
								<span
									class="material-symbols-outlined text-orange-600 text-base"
								>
									date_range
								</span>
								<span class="text-sm font-medium text-gray-700">
									Fechas de vuelo
								</span>
							</div>
							<StyledDateInput
								:modelValue="filtres.dateRange"
								label=""
								placeholder="Seleccionar rango de fechas"
								:range="true"
								:auto-apply="true"
								@update:modelValue="
									(value: any) =>
										emit('update:filtres', { ...filtres, dateRange: value })
								"
							/>
							<p class="text-xs text-gray-500">
								Filtra por fechas de salida o regreso
							</p>
						</div>

						<!-- Fecha de creacion -->
						<div class="space-y-2">
							<div class="flex items-center gap-2 mb-2">
								<span
									class="material-symbols-outlined text-orange-600 text-base"
								>
									add_circle
								</span>
								<span class="text-sm font-medium text-gray-700">
									Período de creación
								</span>
							</div>
							<StyledSelect
								:modelValue="filtres.creationDatePreset"
								label=""
								:options="
									[{ label: 'Seleccione una opcion', value: '' }]
										.concat(optionsFilterMoths)
										.filter((i) => !i.value.includes('monthNext'))
										.filter((i) => !i.value.includes('year'))
								"
								placeholder="Seleccione una opcion"
								@update:modelValue="
									(value: string) =>
										emit('update:filtres', {
											...filtres,
											creationDatePreset: value,
										})
								"
							/>
							<p class="text-xs text-gray-500">
								Filtra por cuando se crearon las reservas (opcional)
							</p>
						</div>

						<!-- Rango de creacion personalizado -->
						<div
							v-if="filtres.creationDatePreset === 'custom'"
							class="space-y-2"
						>
							<div class="flex items-center gap-2 mb-2">
								<span
									class="material-symbols-outlined text-orange-600 text-base"
								>
									event
								</span>
								<span class="text-sm font-medium text-gray-700">
									Fechas de creación
								</span>
							</div>
							<StyledDateInput
								:modelValue="filtres.creationDateRange"
								label=""
								placeholder="Seleccionar rango de fechas de creación"
								:range="true"
								:auto-apply="true"
								@update:modelValue="
									(value: any) =>
										emit('update:filtres', {
											...filtres,
											creationDateRange: value,
										})
								"
							/>
						</div>
					</div>
				</div>

				<!-- Viajero -->
				<div class="relative">
					<div
						class="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
					>
						<div class="flex items-center gap-3 mb-4">
							<div class="p-2 bg-green-100 rounded-lg">
								<span class="material-symbols-outlined text-green-600 text-xl">
									person
								</span>
							</div>
							<h4 class="font-semibold text-gray-900">
								Busqueda por Viajero
							</h4>
						</div>

						<StyledCombobox
							v-model="selectedUser"
							@update:modelValue="
								(value: User | null) =>
									emit('update:filtres', {
										...filtres,
										searchTravelerId: value?.id || null,
										searchTravelerName: value?.fullName || null,
									})
							"
							label="Nombre del viajero"
							placeholder="Buscar por nombre del viajero"
							:options="users?.map((user) => user) || []"
							:loading-options="loadingUsers || isFetchingUsers"
							:on-input-change="
								(e) => {
									setUsersQuery(e)
									refetchUsers()
								}
							"
							:show-input="!selectedUser"
							:prevent-auto-focus="true"
							:prevent-scroll-on-focus="false"
							class="mb-2"
						>
							<template #default="{ option }">
								<span>{{ option.fullName }}</span>
							</template>

							<template #input-overlap>
								<div class="flex items-center justify-between gap-2 w-full">
									<p class="text-sm normal-case md:text-base truncate">
										{{ selectedUser?.fullName }}
									</p>
									<button
										class="material-symbols-outlined text-[22px] hover:text-gray-700 transition-colors"
										@click.prevent="
											() => {
												emit('update:filtres', {
													...filtres,
													searchTravelerId: null,
													searchTravelerName: null,
												})
												selectedUser = null
											}
										"
									>
										close
									</button>
								</div>
							</template>
						</StyledCombobox>
					</div>
				</div>

				<!-- Ordenamiento -->
				<div
					class="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
				>
					<div
						class="bg-gradient-to-r from-green-50 to-teal-50 px-6 py-4 border-b border-gray-200"
					>
						<div class="flex items-center gap-3">
							<div class="p-2 bg-green-100 rounded-lg">
								<span
									class="material-symbols-outlined text-green-600 text-xl"
								>
									sort
								</span>
							</div>
							<h4 class="font-semibold text-gray-900">Ordenamiento</h4>
						</div>
					</div>

					<div class="p-6 space-y-6">
						<div class="space-y-2">
							<span class="text-sm font-medium text-gray-700">
								Ordenar por
							</span>
							<StyledSelect
								:modelValue="filtres.orderBy"
								label=""
								:options="[
									{ label: 'Fecha de creación', value: 'created_at' },
									{ label: 'Fecha de salida', value: 'start_date' },
									{ label: 'Fecha de regreso', value: 'end_date' },
									{ label: 'Total', value: 'total' },
									{ label: 'Nombre del viajero', value: 'traveler_name' },
									{ label: 'Aerolínea', value: 'suppliable_name' },
								]"
								@update:modelValue="
									(value: string) =>
										emit('update:filtres', { ...filtres, orderBy: value })
								"
							/>
						</div>

						<div class="space-y-2">
							<span class="text-sm font-medium text-gray-700">
								Direccion
							</span>
							<StyledSelect
								:modelValue="filtres.orderDirection"
								label=""
								:options="[
									{ label: 'Ascendente', value: 'asc' },
									{ label: 'Descendente', value: 'desc' },
								]"
								@update:modelValue="
									(value: 'asc' | 'desc') =>
										emit('update:filtres', {
											...filtres,
											orderDirection: value,
										})
								"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Footer -->
		<template #footer>
			<div
				class="bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-200"
			>
				<div class="flex items-center justify-center gap-4 p-4 w-full">
					<button
						@click.prevent="
							() => {
								emit('update:filtres', {
									...filtres,
									statusFilter: 'all',
									datePreset: '3months',
									creationDatePreset: '',
									orderBy: 'created_at',
									orderDirection: 'desc',
								})
								selectedUser = null
							}
						"
						class="group inline-flex items-center justify-center px-5 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 hover:scale-105 active:scale-95"
					>
						<span
							class="material-symbols-outlined mr-2 text-lg group-hover:rotate-180 transition-transform duration-300"
						>
							refresh
						</span>
						<span>Limpiar filtros</span>
					</button>
				</div>
			</div>
		</template>
	</SlideOver>
</template>
